import type { Strings } from './index';

/**
 * The Dutch copy. Typed as `Strings`, so it cannot drift from `en.ts`:
 * a line added there is a compile error here until it is translated.
 *
 * Two choices worth stating, because they recur:
 *
 * - *streak* is **reeks**, and the daily counter reads "N dagen op rij" —
 *   "N-dagenreeks" is the literal translation and reads like a chemistry
 *   term.
 * - *Journey* is **Reis**, the mode's name, capitalised in running text so
 *   it stays a proper noun rather than an ordinary trip.
 */
export const nl: Strings = {
  common: {
    close: 'Sluiten',
    menu: 'Menu',
    openMenu: 'Menu openen',
    statistics: 'Statistieken',
    backToToday: 'Terug naar de puzzel van vandaag',
    toToday: '← Puzzel van vandaag',
    viewResult: 'Bekijk resultaat',
    tryAgain: 'Probeer opnieuw',
    copied: 'Gekopieerd naar klembord',
    shareFailed: 'Kon resultaat niet delen',
    loading: 'Laden…',
  },

  header: {
    guessGrid: 'Speelraster',
    keyboard: 'Toetsenbord',
    backspace: 'Backspace',
    enter: 'ENTER',
    boardLocked: 'Bord vergrendeld',
    keyLabel: (letter, state) => `${letter}, ${state}`,
    keyState: {
      correct: 'juiste plek',
      present: 'verkeerde plek',
      absent: 'niet in het woord',
      unused: 'ongebruikt',
    },
  },

  menu: {
    title: 'Menu',
    todaysPuzzle: 'Puzzel van vandaag',
    journey: 'Reis',
    newChallenge: 'Nieuwe uitdaging',
    myChallenges: 'Mijn uitdagingen',
    howToPlay: 'Zo speel je',
    about: 'Over',
    language: 'Taal',
    languageHint: 'Puzzels, definities en de app zelf. Voortgang wordt per taal bijgehouden.',
  },

  daily: {
    sectionLabel: 'Dagpuzzel',
    heading: 'PUZZEL VAN VANDAAG',
    letters: (n) => `${n} LETTERS`,
    loading: 'Puzzel van vandaag laden…',
    challengeAFriend: 'Daag een vriend uit',
  },

  stats: {
    yourStats: 'Jouw statistieken',
    dayStreak: 'DAGEN OP RIJ',
    wins: 'GEWONNEN',
    totalXp: 'TOTALE XP',
    streakLabel: (n) => `${n} dagen op rij`,
    winsLabel: (n) => `${n} keer gewonnen`,
    xpLabel: (n) => `${n} XP in totaal`,
    played: 'GESPEELD',
    winPercent: 'WINST %',
    streak: 'REEKS',
    max: 'MAX',
    totalXpRow: 'Totale XP',
    distribution: 'VERDELING POGINGEN',
    distributionLabel: 'Verdeling van pogingen',
    noGames: 'Speel een spel om je statistieken op te bouwen.',
    distributionRow: (guesses, count) => `${guesses} pogingen: ${count}`,
  },

  board: {
    guessOf: (n, total) => `Poging ${n} van ${total}`,
    solvedIn: (n) => `Opgelost in ${n} pogingen`,
    outOfGuessesWord: (word) => `Geen pogingen meer. Het woord was ${word}`,
  },

  hint: {
    label: 'HINT',
    perHint: '−10 XP per stuk',
    left: (n) => `Hint, nog ${n}`,
    groupLabel: (revealed, total) => `Onthulde hintletters, ${revealed} van ${total} onthuld`,
    position: (index, letter) => `positie ${index}: ${letter}`,
    allRevealed: 'Alle letters zijn al onthuld',
  },

  insight: {
    label: 'INZICHT',
    left: (n) => `Inzicht, nog ${n}`,
    title: 'Woordinzicht',
    guessFirst: 'Raad eerst een woord — inzichten leggen woorden uit die je al gespeeld hebt.',
    reveal: 'Onthullen',
    revealFor: (word) => `Onthul het inzicht voor ${word}`,
    noEntry: 'Geen vermelding',
    unavailable: 'Definitie offline niet beschikbaar.',
    origin: (text) => `Herkomst: ${text}`,
    leftToday: (n) => `Nog ${n} ${n === 1 ? 'inzicht' : 'inzichten'} vandaag`,
  },

  toast: {
    invalidWord: 'Staat niet in de woordenlijst',
    badLength: 'Te weinig letters',
    gameOver: 'Dit spel is afgelopen',
    validationPending: 'Woordenlijst controleren…',
  },

  result: {
    winHeadings: [
      'Geniaal', 'Briljant', 'Indrukwekkend', 'Degelijk', 'Oef', 'Net op tijd',
      'Nagelbijter', 'Aan een zijden draadje',
    ],
    solved: 'Opgelost',
    lost: 'Geen pogingen meer',
    dailySubtitle: (id) => `Lexicon #${id}`,
    dailySubtitleNoNumber: 'Lexicon',
    journeySubtitle: (letters) => `Reis — ${letters} letters`,
    challengeSubtitle: (code) => `Uitdaging ${code}`,
    answerLabel: 'Antwoord',
    noEntry: 'Geen woordenboekvermelding voor dit woord.',
    xpLabel: 'XP verdiend',
    base: 'Basis',
    streakBonus: 'Reeksbonus',
    journeyComplete: 'Reis voltooid',
    hints: 'Hints',
    dayStreak: 'Dagen op rij',
    journeyNoStreak: 'De Reis verandert je reeks nooit',
    challengeNoStreak: 'Uitdagingen veranderen je reeks nooit',
    share: 'Deel resultaat',
  },

  journey: {
    label: 'REIS',
    sectionLabel: 'Reis',
    loading: 'Je Reis laden…',
    rungLabel: (rung, final) => `Reistrede ${rung} van ${final}`,
    complete: 'Reis voltooid!',
    completedCount: (n) => `×${n} voltooid`,
    best: (rung) => `BESTE ${rung}`,
    startNew: 'Begin een nieuwe Reis',
    nextWord: (letters) => `Volgend woord — ${letters} letters`,
    nextFailed: 'Kon het volgende woord niet starten — probeer opnieuw.',
  },

  challenge: {
    label: 'UITDAGING',
    sectionLabel: 'Uitdaging',
    from: (name) => `van ${name}`,
    code: (code) => `Code ${code}`,
    offline: 'OFFLINE',
    loading: 'Uitdaging laden…',
    incompleteLink: 'Deze uitdagingslink is onvolledig.',
    expiredLink: 'Deze uitdagingslink is verlopen of bestaat niet.',
    loadFailed: 'Kon deze uitdaging niet laden.',
    createYourOwn: 'Maak je eigen uitdaging',
    sending: 'Je resultaat versturen…',
    sentAs: (name) => `Resultaat verstuurd als ${name}`,
    sentAnonymously: 'Resultaat anoniem verstuurd',
    sendFailed: 'Resultaat kon niet worden verstuurd',
    offlineNotSent: 'Offline — resultaat niet verstuurd',
    invite: (url) => `Kun jij mijn Lexicon-uitdaging oplossen? ${url}`,
  },

  newChallenge: {
    title: 'Nieuwe uitdaging',
    wordLabel: 'UITDAAGWOORD',
    wordPlaceholder: (min, max) => `woord van ${min}–${max} letters`,
    nameLabel: 'JOUW NAAM (OPTIONEEL)',
    namePlaceholder: 'Anoniem',
    create: 'Uitdaging maken',
    creating: 'Maken…',
    lengthError: (min, max) => `Voer een woord van ${min} tot ${max} letters in`,
    listError: 'Kon de woordenlijst niet laden. Probeer opnieuw.',
    notInList: 'Staat niet in de woordenlijst',
    rateLimited: 'Je hebt de uitdagingslimiet van vandaag bereikt. Probeer het morgen opnieuw.',
    serverError: 'Kon de server niet bereiken.',
    ready: 'Uitdaging klaar',
    readyLabel: 'Uitdaging klaar',
    offlineTitle: 'Offlinelink',
    offlineLabel: 'Offlinelink',
    offlineBody: 'Je vriend kan met deze link nog steeds spelen, maar zijn of haar resultaat komt niet terug.',
    shareLink: 'Deel link',
    copyLink: 'Kopieer link',
    wordLanguageNote: (language) => `Gecontroleerd met de ${language} woordenlijst.`,
  },

  myChallenges: {
    title: 'Mijn uitdagingen',
    none: 'Nog geen uitdagingen.',
    create: 'Maak een uitdaging',
    label: (code) => `Uitdaging ${code}`,
    meta: (code, date) => `Code ${code} · ${date}`,
    loadingResults: 'Resultaten laden…',
    resultsFailed: 'Kon resultaten niet laden',
    retry: 'Opnieuw',
    noPlayers: 'Nog niemand heeft gespeeld',
    anonymous: 'Anoniem',
    solvedIn: (name, guesses, rows) => `${name} — opgelost in ${guesses}/${rows}`,
    didNotSolve: (name) => `${name} — niet opgelost`,
    open: 'Openen',
    shareLink: 'Deel link',
  },

  howTo: {
    title: 'Zo speel je',
    intro: 'Raad het woord van de dag in één poging meer dan het letters heeft.',
    realWord: 'Elke poging moet een bestaand woord zijn. Tegels verkleuren om te laten zien hoe dichtbij je zat.',
    example: {
      correct: 'Groen — juiste letter, juiste plek.',
      present: 'Goud — juiste letter, verkeerde plek.',
      absent: 'Donker — zit niet in het woord.',
    },
    exampleLetters: ['W', 'O', 'D'],
    ladder: 'Elke dag om middernacht komt er een nieuw woord vrij, en de lengte volgt de week: vier '
      + 'letters op maandag, vijf van dinsdag tot en met donderdag, zes op vrijdag en zaterdag, zeven '
      + 'op zondag. Dagen op rij winnen bouwt je reeks op.',
    journey: 'De Reis is de eindeloze modus: klim vier, vijf, zes en zeven letters achter elkaar om een '
      + 'Reis te voltooien. Verliezen zet je terug op vier. Reisspellen leveren de helft van de XP op '
      + 'en raken je reeks nooit.',
    hints: 'Hints onthullen een letter voor −10 XP. Inzichten leggen een woord uit dat je al geraden '
      + 'hebt. Je krijgt 2 hints en 3 inzichten per dag.',
    languages: 'Speel in het Nederlands of het Engels — het menu wisselt de puzzel, de definities en de '
      + 'app zelf. Elke taal houdt zijn eigen reeks, statistieken en XP bij.',
  },

  about: {
    title: 'Over',
    tagline: 'Lexicon — scherp je geest. Verrijk je woordenschat.',
    body: 'Een woordspel waarvan de woordlengte met de week meebeweegt, met reeksen, XP, hints, '
      + 'woordinzichten, een eindeloze Reis-modus en uitdagingen die je naar een vriend kunt sturen. '
      + 'Speelbaar in het Nederlands en het Engels.',
    version: 'Versie 1.1',
    privacy: 'Je reeks, statistieken en voortgang staan alleen op dit apparaat. Geen account. De app '
      + 'stuurt één anonieme speelping per dag zodat we spelers kunnen tellen — nooit persoonlijke '
      + 'gegevens.',
    creditsEnglish: 'Engelse definities en etymologieën komen van Wiktionary via het kaikki.org-extract, gebruikt onder',
    creditsDutch: 'Nederlandse definities komen van Open Dutch WordNet, eveneens onder CC BY-SA 4.0. '
      + 'De Nederlandse woordenlijsten komen van de OpenTaal-woordenlijst.',
    wordLists: 'De Engelse woordenlijsten zijn afgeleid van de originele Wordle-woordenlijsten.',
  },

  notFound: {
    title: 'Hier is niets',
    body: 'Die link leidt nergens heen in Lexicon.',
  },
};
